import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Modal,
  Radio,
  RadioGroup,
  SxProps,
  Theme,
} from '@mui/material';
import React, {useMemo, useState} from 'react';
import styles from '../../styles';
import {useTranslation} from 'react-i18next';
import {
  getTeamSessionExport,
  getTeamSessionGroupExport,
  fetchPlayerSessionExportData,
  fetchTeamSessionExportData,
  fetchTeamSessionGroupExportData,
} from '../../api/exportAPI';
import {TeamSessionExportTypes} from '../../enums';
import {
  downloadReportCsv,
  formatPlayerSessionExportData,
  formatShiftExportData,
  aggregatePlayerSessionExportData,
  PlayerSessionExportData,
} from '../../services/reportService';
import {useAppSelector} from '../../store/hooks';
import {DateTime} from 'luxon';

interface Props {
  playerSessionIds?: number[];
  playerSessionStartTime?: string;
  teamSessionId?: number;
  teamSessionStartTime?: string;
  teamSessionHasPuckDropEvents?: boolean;
  teamSessionGroupId?: number;
  teamSessionGroupStartTime?: string;
  teamHasHudlConfig?: boolean;
  teamHasSpiideoConfig?: boolean;
  teamHasCatapultConfig?: boolean;
  buttonSx?: SxProps<Theme>;
}

const ExportSessionModal: React.FC<Props> = ({
  playerSessionIds,
  playerSessionStartTime,
  teamSessionId,
  teamSessionStartTime,
  teamSessionHasPuckDropEvents,
  teamSessionGroupId,
  teamSessionGroupStartTime,
  teamHasHudlConfig,
  teamHasSpiideoConfig,
  teamHasCatapultConfig,
  buttonSx,
}) => {
  const {t} = useTranslation();
  const preferredUnits = useAppSelector(
    state => state.profile.profile?.preferredUnits ?? 'imperial',
  );
  const [showExportSessionModal, setShowExportSessionModal] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [downloadType, setDownloadType] = useState<
    | TeamSessionExportTypes.csvPlayers
    | TeamSessionExportTypes.csvShifts
    | TeamSessionExportTypes.hudlSportscode
    | TeamSessionExportTypes.spiideoPerform
    | TeamSessionExportTypes.catapultThunder
  >(TeamSessionExportTypes.csvShifts);

  // Helper to generate report name based on context
  const getReportName = (format: string) => {
    const startTime =
      playerSessionStartTime ||
      teamSessionStartTime ||
      teamSessionGroupStartTime;
    const dateStr = startTime
      ? DateTime.fromISO(startTime).toFormat('yyyy-MM-dd-HH-mm')
      : DateTime.local().toFormat('yyyy-MM-dd-HH-mm');

    if (playerSessionIds?.length) {
      return `playersession-${format === TeamSessionExportTypes.csvPlayers ? 'players' : 'shifts'}-${dateStr}`;
    } else if (teamSessionGroupId) {
      return `teamsessiongroup-${format === TeamSessionExportTypes.csvPlayers ? 'players' : 'shifts'}-${dateStr}`;
    } else if (teamSessionId) {
      return `teamsession-${format === TeamSessionExportTypes.csvPlayers ? 'players' : 'shifts'}-${dateStr}`;
    }
    return `export-${dateStr}`;
  };

  // Handle CSV export using browser-based generation
  const handleCsvExport = async (
    format:
      | TeamSessionExportTypes.csvPlayers
      | TeamSessionExportTypes.csvShifts,
  ) => {
    setIsLoading(true);
    try {
      let playerSessions: PlayerSessionExportData[] = [];

      // Fetch the data based on context
      if (playerSessionIds?.length) {
        playerSessions = await fetchPlayerSessionExportData(playerSessionIds);
      } else if (teamSessionGroupId) {
        const groupData =
          await fetchTeamSessionGroupExportData(teamSessionGroupId);
        playerSessions = groupData.teamSessions.flatMap(
          ts => ts.playerSessions || [],
        );
      } else if (teamSessionId) {
        const sessionData = await fetchTeamSessionExportData(teamSessionId);
        playerSessions = sessionData.playerSessions || [];
      }

      if (playerSessions.length === 0) {
        console.error('No player sessions found for export');
        return;
      }

      let exportData: Record<string, string | number | null>[] = [];

      if (format === TeamSessionExportTypes.csvPlayers) {
        // For team session exports, aggregate by player
        if (teamSessionId || teamSessionGroupId) {
          // Group sessions by player
          const sessionsByPlayer: Record<number, PlayerSessionExportData[]> =
            {};
          playerSessions.forEach(ps => {
            const playerId = ps.playerNumber || ps.id || 0;
            const key = `${ps.firstName}-${ps.lastName}-${playerId}`;
            if (!sessionsByPlayer[key as unknown as number]) {
              sessionsByPlayer[key as unknown as number] = [];
            }
            sessionsByPlayer[key as unknown as number].push(ps);
          });

          // Aggregate each player's sessions
          exportData = Object.values(sessionsByPlayer).map(sessions =>
            aggregatePlayerSessionExportData(sessions, preferredUnits),
          );
        } else {
          // For individual player session export, format each session
          exportData = playerSessions.map(ps =>
            formatPlayerSessionExportData(ps, preferredUnits),
          );
        }
      } else {
        // Shifts export - flatten all shifts from all sessions
        exportData = playerSessions.flatMap(ps => {
          const name = `${ps.firstName || ''} ${ps.lastName || ''}`.trim();
          const position = ps.profilePosition || ps.position;
          return (ps.playerSessionShifts || []).map(shift =>
            formatShiftExportData(
              {
                ...shift,
                name,
                position,
                type: ps.type,
                timeZone: ps.timeZone,
                teamSessionId: ps.teamSessionId,
              },
              preferredUnits,
            ),
          );
        });
      }

      // Download the CSV
      downloadReportCsv({
        exportReportData: exportData,
        reportName: getReportName(format),
      });
    } catch (err) {
      console.error('CSV export error:', err);
    } finally {
      setIsLoading(false);
      setShowExportSessionModal(false);
    }
  };

  // Handle XML exports (keep using API)
  const handleXmlExport = async (
    format:
      | TeamSessionExportTypes.hudlSportscode
      | TeamSessionExportTypes.spiideoPerform
      | TeamSessionExportTypes.catapultThunder,
  ) => {
    setIsLoading(true);
    try {
      if (teamSessionId && teamSessionStartTime) {
        await getTeamSessionExport({
          teamSessionId,
          teamSessionStartTime,
          format,
        });
      } else if (teamSessionGroupId && teamSessionGroupStartTime) {
        await getTeamSessionGroupExport({
          teamSessionGroupId,
          teamSessionGroupStartTime,
          format,
        });
      }
    } catch (err) {
      console.error('XML export error:', err);
    } finally {
      setIsLoading(false);
      setShowExportSessionModal(false);
    }
  };

  const downloadReport = async () => {
    if (
      [
        TeamSessionExportTypes.csvPlayers,
        TeamSessionExportTypes.csvShifts,
      ].includes(downloadType)
    ) {
      await handleCsvExport(
        downloadType as
          | TeamSessionExportTypes.csvPlayers
          | TeamSessionExportTypes.csvShifts,
      );
    } else {
      await handleXmlExport(
        downloadType as
          | TeamSessionExportTypes.hudlSportscode
          | TeamSessionExportTypes.spiideoPerform
          | TeamSessionExportTypes.catapultThunder,
      );
    }
  };

  const showNoPuckDropEventsError = useMemo(() => {
    return (
      !teamSessionHasPuckDropEvents &&
      [
        TeamSessionExportTypes.hudlSportscode,
        TeamSessionExportTypes.spiideoPerform,
        TeamSessionExportTypes.catapultThunder,
      ].includes(downloadType)
    );
  }, [downloadType, teamSessionHasPuckDropEvents]);

  const showNoHudlConfigError = useMemo(() => {
    return (
      !teamHasHudlConfig &&
      downloadType === TeamSessionExportTypes.hudlSportscode
    );
  }, [downloadType, teamHasHudlConfig]);

  const showNoSpiideoConfigError = useMemo(() => {
    return (
      !teamHasSpiideoConfig &&
      downloadType === TeamSessionExportTypes.spiideoPerform
    );
  }, [downloadType, teamHasSpiideoConfig]);

  const showNoCatapultConfigError = useMemo(() => {
    return (
      !teamHasCatapultConfig &&
      downloadType === TeamSessionExportTypes.catapultThunder
    );
  }, [downloadType, teamHasCatapultConfig]);

  return (
    <>
      <Button
        variant="contained"
        sx={{mr: 2, ...buttonSx}}
        onClick={() => setShowExportSessionModal(true)}>
        {t('general.export')}
      </Button>
      <Modal
        open={showExportSessionModal}
        onClose={() => {
          if (!isLoading) {
            setShowExportSessionModal(false);
          }
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <FormControl disabled={isLoading}>
            <FormLabel id="export-radio-buttons-group-label">
              {t('general.selectExportType')}
            </FormLabel>
            <RadioGroup
              onChange={v =>
                setDownloadType(
                  v.target.value as
                    | TeamSessionExportTypes.csvPlayers
                    | TeamSessionExportTypes.csvShifts
                    | TeamSessionExportTypes.hudlSportscode
                    | TeamSessionExportTypes.spiideoPerform
                    | TeamSessionExportTypes.catapultThunder,
                )
              }
              aria-labelledby="export-radio-buttons-group-label"
              value={downloadType}
              name="radio-buttons-group">
              <FormControlLabel
                value={TeamSessionExportTypes.csvPlayers}
                control={<Radio />}
                label={t('export.playersCsv')}
              />
              <FormControlLabel
                value={TeamSessionExportTypes.csvShifts}
                control={<Radio />}
                label={t('export.shiftsCsv')}
              />
              {(teamSessionId || teamSessionGroupId) && (
                <>
                  <FormControlLabel
                    value={TeamSessionExportTypes.hudlSportscode}
                    control={
                      <Radio disabled={!!teamSessionGroupId || isLoading} />
                    }
                    label={t('export.hudlSportscode')}
                  />
                  <FormControlLabel
                    value={TeamSessionExportTypes.spiideoPerform}
                    control={
                      <Radio disabled={!!teamSessionGroupId || isLoading} />
                    }
                    label={t('export.spiideoPerform')}
                  />
                  <FormControlLabel
                    value={TeamSessionExportTypes.catapultThunder}
                    control={
                      <Radio disabled={!!teamSessionGroupId || isLoading} />
                    }
                    label={t('export.catapultThunder')}
                  />
                </>
              )}
              {teamSessionGroupId && (
                <Alert severity="info" variant="outlined" sx={{my: 1}}>
                  {t('export.exportTeamSessionGroupInfo')}
                </Alert>
              )}
            </RadioGroup>
          </FormControl>
          {/* errors */}
          {showNoPuckDropEventsError && (
            <Alert severity="error" variant="outlined" sx={{mb: 1}}>
              {t('export.errorMissingTeamSessionSessionEvents')}
            </Alert>
          )}
          {showNoHudlConfigError && (
            <Alert severity="error" variant="outlined" sx={{mb: 1}}>
              {t('export.errorMissingTeamHudlConfig')}
            </Alert>
          )}
          {showNoSpiideoConfigError && (
            <Alert severity="error" variant="outlined" sx={{mb: 1}}>
              {t('export.errorMissingTeamSpiideoConfig')}
            </Alert>
          )}
          {showNoCatapultConfigError && (
            <Alert severity="error" variant="outlined" sx={{mb: 1}}>
              {t('export.errorMissingTeamCatapultConfig')}
            </Alert>
          )}
          <Box sx={{mt: 2, display: 'flex', alignItems: 'center', gap: 2}}>
            <Button
              disabled={
                isLoading ||
                showNoPuckDropEventsError ||
                showNoHudlConfigError ||
                showNoSpiideoConfigError ||
                showNoCatapultConfigError
              }
              onClick={downloadReport}>
              {t('general.export')}
            </Button>
            {isLoading && <CircularProgress size={24} />}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ExportSessionModal;
